// Every word on the Coilmind landing page, plus the three facts the copy deck
// (COILMIND_HOMEPAGE_COPY_DECK.md, Aug 2026) marks as release blockers.
//
// Why the copy lives here and not in Landing.jsx: the trial terms, the founder
// numbers and the demo videos are all still open decisions, and each of them is
// referenced from three or four places on the page. Keeping them in one module
// means changing the offer is a one-line edit instead of a hunt through 1 400
// lines of JSX — and nothing can drift out of sync in between.

// ── BLOCKER 1: the trial offer ──────────────────────────────────────────────
// Switch TRIAL_MODEL and the hero, the pricing footer, the final CTA and the
// FAQ answer all follow. Nothing else needs touching on the page.
//
//   'card'      — PayPro issues the trial, card up front. What the backend does
//                 today.
//   'no-card'   — ACTIVE: the offer both copy decks are written against.
//                 ⚠️ NEEDS BACKEND WORK BEFORE PUBLISHING: trial issued by our
//                 own backend (not PayPro), a 25-task counter, and the trial
//                 bound to the machine's hardware id rather than the email —
//                 bound to the email it is farmable by re-registration.
//   'free-read' — read/analyse forever, writing needs a plan. Largest backend
//                 change of the three (a capability-limited licence tier).
const TRIAL_MODEL = 'no-card';

const TRIAL_MODELS = {
  card: {
    days: 14,
    cta: 'Start your 14-day free trial',
    micro: '14-day free trial · card required · cancel anytime before it ends.',
    faq: 'Every plan starts with a 14-day free trial; a card is required and you can cancel anytime before it ends so you’re not charged.',
  },
  'no-card': {
    days: 14,
    taskGrant: 25,
    cta: 'Start your 14-day free trial',
    micro: '14-day free trial · no credit card · 25 tasks included',
    faq: 'Every plan starts with a 14-day free trial with 25 tasks included and no credit card. You add a card only if you decide to keep it.',
  },
  'free-read': {
    cta: 'Download Coilmind (free)',
    micro: 'Free forever: read and analyse your project. No card, no time limit.',
    faq: 'Reading and analysing your project (cross-references, call graphs, block explanations, code review) is free forever, with no card and no time limit. Generating logic and writing to the project needs a plan.',
  },
};

export const TRIAL = TRIAL_MODELS[TRIAL_MODEL];

// ── BLOCKER 2: svghmi.pro credibility facts ─────────────────────────────────
// The copy deck is explicit: invented numbers in this niche get caught in the
// first sales conversation. Every field is null until the owner supplies a real
// figure — the page renders the shorter, still-true sentence instead of a gap.
export const BRAND = {
  sinceYear: null, // e.g. 2023 → "tools for Siemens engineers since 2023"
  engineers: null, // e.g. '1,200' → stat tile "engineers using svghmi tools"
  countries: null, // e.g. 40     → stat tile "countries shipped to"
};

// The spec strip's last chip. The parent brand is the answer to "who are you",
// so it stays loud even before the year is known.
export const BRAND_CHIP = BRAND.sinceYear
  ? `svghmi.pro · tools for Siemens engineers since ${BRAND.sinceYear}`
  : 'svghmi.pro · tools for Siemens engineers';

export const BRAND_STATS = [
  BRAND.engineers && { value: BRAND.engineers, label: 'engineers using\nsvghmi tools' },
  BRAND.sinceYear && { value: `since ${BRAND.sinceYear}`, label: 'selling tools to\nSiemens engineers' },
  BRAND.countries && { value: BRAND.countries, label: 'countries\nshipped to' },
].filter(Boolean);

// Coilmind's own media sits next to the installer on the API host, which Caddy
// serves straight off disk. The version is in the filename on purpose: a
// replacement reel gets a new URL rather than fighting a cached copy.
const MEDIA = 'https://server.svghmi.pro/media/coilmind';

// Short silent loops — ten seconds each, no audio track — that sit inside the
// block making the claim rather than in a gallery of their own.
//
// Each is mastered at twice the width of the slot it plays in and cropped to
// its content, which is why `ratio` is a real pixel pair rather than a tidy
// 16/9: padding would only buy empty frame at the cost of legible type, and
// type here lands at half its authored size. None of them carries a title —
// the page prints the heading beside it, and nothing should be said twice.
export const CLIPS = {
  offline: {
    src: `${MEDIA}/clips/coilmind-offline-v3.mp4`,
    poster: `${MEDIA}/clips/coilmind-offline-v3-poster.jpg`,
    ratio: '1480 / 380',
    // Draws its own panel, so the page must not draw a second one around it.
    bare: true,
    alt: 'TIA Portal, Coilmind and a local model wired together inside one engineering PC, with the link out to a cloud AI provider cut',
  },
  doesWork: {
    src: `${MEDIA}/clips/coilmind-doeswork-v1.mp4`,
    poster: `${MEDIA}/clips/coilmind-doeswork-v1-poster.jpg`,
    ratio: '1480 / 406',
    alt: 'A plan ticking itself off: the tag check done, interface members and the dry-run networks queued behind it',
  },
  plcsim: {
    src: `${MEDIA}/clips/coilmind-plcsim-v3.mp4`,
    poster: `${MEDIA}/clips/coilmind-plcsim-v3-poster.jpg`,
    ratio: '1480 / 592',
    alt: 'A test run going from two failed assertions to two passing after Coilmind fixes the expectation',
  },
  safety: {
    src: `${MEDIA}/clips/coilmind-safety-v3.mp4`,
    poster: `${MEDIA}/clips/coilmind-safety-v3-poster.jpg`,
    ratio: '2248 / 600',
    bare: true,
    alt: 'A terminal taking a snapshot, failing the build, rolling back automatically and reporting the block unchanged',
  },
};

// ── The demo videos ─────────────────────────────────────────────────────────
// Both are self-hosted and both cost nothing until they are asked for: the reel
// autoplays muted where that is welcome, the full run waits for a click, since
// ten megabytes is not a thumbnail.
export const VIDEO = {
  // The hero reel is self-hosted rather than embedded: it has to be able to
  // start by itself above the fold, and an embed cannot do that without pulling
  // the player in first. The file has no audio track at all — every word in it
  // is a burnt-in caption, which `note` says out loud so nobody goes hunting for
  // a volume control that would do nothing.
  hero: {
    src: `${MEDIA}/hero/coilmind-hero-v2.mp4`,
    poster: `${MEDIA}/hero/coilmind-hero-v2-poster.jpg`,
    duration: '1:09',
    note: 'Screen recording, no audio. The captions are on screen.',
  },
  fullRun: {
    src: `${MEDIA}/full-run/coilmind-full-run-v2.mp4`,
    poster: `${MEDIA}/full-run/coilmind-full-run-v2-poster.jpg`,
    // Two numbers, both true and both needed: the file runs 3:07, the session it
    // came from was recorded for 9:36. Chapter times are the file's, because
    // that is what a viewer scrubs; the claim being made is about the session.
    duration: '3:07',
    timeline: '3:07 of 9:36 recorded',
    alt: 'One session: dry-run protection written into an existing pump block, then proved against PLCSIM until the tests go green',
  },
};

// Chapter times are the clip's own, because the clip is what the viewer scrubs.
//
// 00:46 is not an apology, it is the argument: the one break in the session is
// named in the timeline, the way it is named on screen. Drop the row and the
// card in the frame still says the recording paused — with nothing beside it to
// explain why.
//
// 02:14 is the moment the recording is worth watching for, and the moment
// easiest to lie about. The first run did not fail because the logic was wrong:
// the test expected DryRun_Trip to hold, and DryRun_Trip is a momentary coil —
// it resets the instant the trip stops the pump, while the state that persists
// lives in DryRun_Alarm. Keep the line about the *test's* expectation. Shorten
// it into "it finds its own mistake" and the page starts claiming something
// that did not happen.
export const FULL_RUN_CHAPTERS = [
  { t: '00:00', s: 0, label: 'The task, typed by hand into the chat' },
  { t: '00:05', s: 5, label: 'It reads the block, then writes three new networks' },
  { t: '00:33', s: 33, label: 'Three networks in the project. Compiled, 0 errors.' },
  { t: '00:46', s: 46, label: 'Recording paused: PLCSIM Advanced started' },
  { t: '00:59', s: 59, label: 'It traces what actually starts the pump' },
  { t: '02:07', s: 127, label: 'First run: 0 passed, 2 failed' },
  {
    t: '02:14', s: 134, label: 'It finds the cause: the test expected a momentary coil', pivot: true,
  },
  { t: '02:44', s: 164, label: 'Second run: 2 / 2 pass' },
];

// ── Page copy ───────────────────────────────────────────────────────────────

export const HERO = {
  // The slogan: Coilmind read as "mind the coil", after the London Underground.
  slogan: 'Mind the coil.',
  eyebrow: 'For Siemens TIA Portal engineers',
  // Pre-empts the "another ChatGPT wrapper" objection before the headline.
  kicker: 'Yes, there is an LLM in it. But you don’t paste code into a chat window: it works inside the TIA project you have open.',
  h1: 'An AI that writes ladder logic into your TIA Portal project,',
  h1Accent: 'and only applies it if it compiles.',
  // Two sentences on purpose: what it does, then why that is safe. The six-item
  // feature list this replaced is all covered by "What Coilmind does" below.
  sub: 'It reads the open project through Openness, writes the blocks, imports them and compiles. If the build fails, the change isn’t applied.',
  // The experienced engineer's first objection: compiling proves syntax, not
  // behaviour. Answered with the recorded run, not a promise.
  proof: 'A clean compile doesn’t tell you the logic does what you meant, so Coilmind also tests it on the simulator:',
  proofLink: 'watch 0 passed → 2 / 2 pass',
};

// The reel replaced the four-minute walkthrough that was never recorded, and
// the section kept the `tour` id and nav link — the promise the page has always
// made. The lead names the chapters burnt into the recording, so the copy and
// the video cannot drift apart.
export const TOUR = {
  eyebrow: 'Walkthrough',
  h2: 'The 70-second tour',
  lead: 'Four chapters, all on one project. It reads the logic, writes three networks that compile, runs them against the simulator and takes a snapshot before each change.',
};

export const COMPAT = ['Windows', 'TIA Portal V17–V21', 'Openness API', '7 UI languages', 'PLCSIM Advanced V3–V6 (tests only, sold by Siemens)'];

export const PAINS = [
  { q: '"Cloning the same block for 12 conveyors by hand, renaming every tag."', tag: '→ block cloning' },
  { q: '"Inherited a 15-year-old project full of STL nobody on the team can read anymore."', tag: '→ legacy STL → SCL' },
  { q: '"Reverse-engineering a block someone else wrote, network by network."', tag: '→ code analysis' },
  { q: '"Translating comments and tags into three languages for the client’s HMI."', tag: '→ translation' },
  { q: '"Editing WinCC Unified scripts in a window with zero IntelliSense."', tag: '→ WinCC editor' },
  { q: '"Copy-pasting XML into ChatGPT and back. It never sees the project, and it can’t change anything in it."', tag: '→ works in the open project' },
];

export const FEATURES = [
  {
    name: 'Analysis',
    badge: 'Lead',
    title: 'Answers questions about the whole project',
    value: 'Ask what an FB does, who writes to a tag, what’s inconsistent. The answer comes from your project, not from general PLC knowledge. It reads pasted screenshots too. Outside the chat there are project dashboards: call graph, architecture and health, instance usage, and which blocks are actually reusable.',
    // safe_to_write was the third word here, and it returns null for blocks with
    // >80 tags and for any dotted name — i.e. most DB members and alarms.
    tag: 'Cross-references, call graph, UDT signatures',
  },
  {
    // Its own tab rather than a line under Analysis: documenting a project is
    // the headline use case for PLC Copilot, and Coilmind does it from the real
    // blocks, not from a description of them.
    name: 'Documentation',
    title: 'Writes the documentation from the LAD and SCL',
    value: 'Point it at a block, a folder or the whole program. It reads the LAD and SCL, works out what the logic does (interlocks, sequences, which block writes to which tag) and writes it up: what each block is for, its interface, and a flowchart in Mermaid. You get a Markdown file that opens in any editor and can go to the client as it is.',
    tag: 'Reads LAD and SCL, Mermaid flowcharts, exports .md',
  },
  {
    name: 'Cloning',
    title: 'Clones a block and renames the tags',
    value: '“Clone FB Motor1 for the second drive, use M2_* tags.” It creates the copy, imports it and compiles, and keeps fixing it until it builds.',
    tag: 'Recursive tag rename, compile-and-fix loop',
  },
  {
    name: 'Generator',
    badge: 'No AI',
    title: 'Twelve conveyors from one template, in one pass',
    value: 'Queue a template against a list. It creates every block, allocates the tags, walks the start addresses and gives you a report of what it made. Deterministic and repeatable, with a history of every run. For conveyors two to twelve it is the fastest route, and no model is involved.',
    tag: 'Batch runs, automatic tags, address walk, run history',
  },
  {
    // Was "Agentic" — the only jargon label in the list, and meaningless to an
    // automation engineer. The tab now says what the panel says.
    name: 'Does the work',
    title: 'Works through the steps itself',
    // "every step is one click to undo" was too broad: snapshots exist for
    // blocks and HMI screens, not for tag, tag-table or UDT operations.
    value: 'You ask in chat. Coilmind writes, imports, compiles and fixes its own errors, and takes the next step itself when it is obvious. Every block and screen it touches is snapshotted first, so a change you don’t like is one click back, and every action is listed in the project changelog.',
    tag: 'Writes, imports, compiles, logs to the changelog',
    media: CLIPS.doesWork,
  },
  {
    name: 'Legacy STL',
    title: 'Rewrites legacy STL as SCL or LAD',
    // There is no automatic equivalence prover: it is a workflow you run, and
    // running it needs PLCSIM Advanced — a separate Siemens purchase.
    value: 'Legacy STL blocks get rewritten in chat. To prove the rewrite behaves like the original, run the original’s tests against it; that part needs PLCSIM Advanced. Anything that doesn’t translate cleanly is shown to you instead of guessed.',
    tag: 'Rewrite in chat, check against the original’s tests',
  },
  {
    name: 'Offline',
    title: 'Works offline, up to 14 days at a time',
    // OFFLINE_GRACE_DAYS = 14 in the desktop app: "weeks" overstated it, and an
    // unqualified "air-gapped" implies forever. The licence still expires.
    value: 'Run a local model on the engineering PC itself, on site or anywhere off the network. Your code stays on the PC. You can work up to 14 days without a connection, then the licence needs a short refresh. Local models are weaker than the cloud ones, and how capable they are depends on your GPU.',
    tag: 'Local model, up to 14 days without a connection',
    media: CLIPS.offline,
  },
  {
    name: 'Translation',
    badge: 'New',
    title: 'Translation that keeps the PLC terminology',
    value: 'Inherited a German or Chinese project? It translates titles, comments and tag descriptions with the logic as context, so you don’t get machine pidgin back.',
    tag: 'EN · DE · IT · ES · FR · RU · ZH',
  },
  {
    name: 'Templater',
    badge: 'No AI',
    title: 'Block templates, no AI involved',
    value: 'Mark the tags and parameters that change, then type the new names. Fully deterministic, so you can use it where AI is off-limits.',
    tag: 'Deterministic, works offline, fully local',
  },
  {
    // This tab used to claim a script editor and nothing else, while the product
    // creates screens, items, dynamizations and HMI tags. Underselling the one
    // capability a buyer pays most for.
    name: 'WinCC Unified',
    title: 'Builds the HMI screen end to end',
    value: 'It creates screens, places items and changes any item’s properties: position, size, colours, text, the tag it shows. It creates the HMI tags, tag tables and groups behind it, sets dynamizations, writes the JavaScript for events and script dynamizations, and compiles the HMI. Scripts you write yourself get a proper editor: Monaco, with IntelliSense from the Siemens V21 definitions and project-wide search. It does not create HMI devices. Faceplate types are out of reach for any tool, because Openness cannot create them.',
    tag: 'Screens, items, properties, HMI tags, JS scripts',
  },
  {
    name: 'PLCSIM tests',
    title: 'Tests stored with the project',
    value: 'Tests are declarative .test.json files: set inputs, run N cycles, assert outputs, and watch the run live. Coilmind can scaffold a suite from a spec and download to the simulator, never to real hardware. This needs PLCSIM Advanced, which Siemens sells separately.',
    tag: '.test.json files, needs PLCSIM Advanced',
    media: CLIPS.plcsim,
  },
  {
    // New in the product, and missing from the page until the SEO plan noticed.
    // Kept general on purpose: the owner wants the idea here, not the setup.
    name: 'Your own agent',
    badge: 'New',
    title: 'Works as the TIA hands for Codex or Claude Code',
    value: 'Connect your own agent, such as Codex or Claude Code, to Coilmind over local MCP. Your agent plans and sets the task; Coilmind does the TIA part with what it knows about Openness, LAD networks and Siemens instructions, and with the same checks as always: dry-run validation, compile gate, snapshot. The conversation between the two agents is visible in Coilmind while it runs.',
    tag: 'Codex, Claude Code and other local MCP clients',
  },
];

// Section leads that were still inline in the JSX. Copy belongs in this file —
// that is the whole point of it — and keeping them here is also what stops the
// prose from being reformatted for line length by someone chasing a linter.
export const FEATURES_LEAD = 'Each of these works on the project you have open in TIA Portal.';

export const DIFF_LEAD = 'It works like the cloud assistants, but it doesn’t expect you to be online all the time, to pay for a year up front or to upgrade to the newest TIA Portal.';

export const SAFETY_LEAD = 'You run production lines, and a wrong change is downtime. An assistant that acts on its own needs a way back, and this is how it works.';

export const WHO = {
  lead: 'Whether you freelance or work in a three-person integrator, the same jobs come round every week: cloning blocks, working out someone else’s logic, translating for the client’s HMI, fixing legacy code on site with the clock running.',
  solo: 'Coilmind takes the repetitive part of the build, so one person can take on more projects and ship sooner.',
  team: 'Shared templates keep everyone’s code to the same standard, so a colleague can pick up a block someone else generated.',
};

export const PROOF = {
  eyebrow: 'Proof',
  h2: 'One session, nothing cut',
  lead: 'Dry-run protection written into an existing pump block, then tested on PLCSIM until it passes: one session, 9 minutes 36 seconds of recording, nothing cut. There is one break of 36 minutes while PLCSIM Advanced was started, and the frame says so. Typing and generation waits are sped up. Nothing else is edited.',
  // Says out loud what the reader is already thinking. Removing this line costs
  // the block most of its force.
  quote: 'If you’ve seen AI demos that fall apart the moment you try them yourself, this is the one to watch.',
};

export const DIFFERENTIATORS = [
  {
    n: '01',
    title: 'With a local model, your code stays on the PC',
    body: 'Run a local model on the PC itself, on site or on an air-gapped network. The block templater needs no model at all. Local models are weaker than the cloud ones, and how far they go depends on your GPU budget.',
    chip: 'OFFLINE · LOCAL MODEL',
  },
  {
    n: '02',
    title: 'Priced per licence, month to month',
    body: 'Pay by card, month to month. No annual lock-in and no org account. AI is included, with no tokens to buy.',
    chip: 'PER-LICENCE · AI INCLUDED',
  },
  {
    n: '03',
    title: 'Works with TIA Portal V17 to V21',
    body: 'On V17 or V18? Coilmind attaches to the project as it is, no forced upgrade to the latest portal. I test day to day on V19–V21, and a few conveniences, block renaming among them, need V21. On the older portals expect the core to work and the edges to be thinner.',
    chip: 'V17 → V21',
  },
];

export const DIFF_CHIPS = [
  '7 UI languages · EN/DE/IT/ES/FR/RU/ZH',
  'No tokens to buy. Your own key or a local model is optional, on every plan',
  'LAD built through Openness, checked by dry-run validation',
];

export const STEPS = [
  'Download, install and sign in on your engineering PC. Coilmind sets up Openness access itself: one Windows prompt, no manual whitelisting.',
  'Open your project in TIA Portal, then attach Coilmind to it. Openness can only attach to a project that is already open, so TIA stays in charge.',
  'Ask in the chat: analyse, generate, clone, translate, test.',
];

export const SAFETY_BULLETS = [
  'Dry-run validation before writing: topology, schema and semantics checked without touching the project',
  'Local snapshot before every block and HMI screen change, restore in one click',
  'Compile gate after every block import: if it doesn’t build, the change isn’t applied',
  'A changelog of every change the assistant made, kept in the project',
];

export const WHO_CHIPS = [
  { t: 'reverse-engineer a handover', accent: false },
  { t: 'clone for a new line', accent: false },
  { t: 'translate for the client', accent: false },
  { t: 'fix legacy on site, offline', accent: true },
];

// ── Who's behind this ───────────────────────────────────────────────────────
// Sits immediately BEFORE pricing, not after: it has to land in the moment the
// reader is deciding whether to pay. Turns the weakest fact about Coilmind (one
// developer, no support org) into the reason to trust it.
const fundingSentence = [
  'Coilmind is not a funded startup looking for an exit. svghmi.pro has been selling tools to Siemens engineers',
  BRAND.sinceYear ? ` since ${BRAND.sinceYear}` : '',
  BRAND.engineers && BRAND.countries ? ` (${BRAND.engineers} engineers in ${BRAND.countries} countries)` : '',
  ', and it runs on subscriptions, with no funding rounds behind it. So Coilmind gets built in the direction users ask for. There is nobody else I answer to.',
].join('');

export const FOUNDER = {
  eyebrow: 'Who’s behind this',
  h2: 'Built by one automation engineer',
  paragraphs: [
    'I spent years writing PLC programs for Siemens hardware, and a lot of those years went on the same jobs done by hand: cloning blocks for the twelfth conveyor, reverse-engineering someone else’s STL, retyping tag names. Coilmind started as a tool I built for myself.',
    fundingSentence,
    'If something breaks, you email me and I answer it myself. There is no support team in between.',
  ],
  signature: '— Cirill, Amsterdam',
  email: 'support@svghmi.pro',
};

// ── Pricing ─────────────────────────────────────────────────────────────────
// "Requests" was ambiguous (does every chat message count?) and the weekly
// window made the allowance sound smaller than it is. Both are fixed in the
// card copy; the honesty about the rolling window lives here so the cards stay
// scannable.
// ⚠️ VERIFY BEFORE PUBLISHING: "metered over a rolling 7-day window" must match
// what the licence server actually counts.
export const PRICING_NOTE = 'A task is one thing you ask for: "clone this FB for the second drive", "rewrite this STL block in SCL". Coilmind may make a dozen internal calls to finish it, and you’re billed for one task. Fair use is metered over a rolling 7-day window, so the monthly figure is an average rather than a burst allowance.';

// Yearly billing exists because Siemens Eigen is annual-only at £1,548.16 per
// seat: at €149/month Premium came to €1,788 a year, close enough to invite the
// comparison on the tier where Coilmind is weaker. At €1,490 it lands under
// their number, and monthly stays available — which is itself the argument.
// ⚠️ BEFORE PUBLISHING: the yearly products must exist in PayPro Global, and
// the account page must carry the chosen period into checkout. Until then a
// yearly CTA lands on a monthly checkout.
export const BILLING_NOTE = 'Pay monthly or yearly. Paying for the year gets you two months free.';

// The page said "per engineer" while the product is one licence across several
// machines, with no objection to colleagues working at once. That understated a
// real advantage: an Eigen seat covers three assigned users but only one at a
// time.
// ⚠️ BEFORE PUBLISHING: the Terms need wording for where "a couple of
// colleagues" ends and "an integrator's fifteen people on one key" begins. The
// rate limit is probably the answer, but it should be written down first.
export const LICENCE_NOTE = 'One licence covers several machines, and two of you can work at the same time. What limits you is requests per minute. There are no named seats.';

export const LANGUAGE_LINE = 'Translation that keeps the PLC terminology (EN · DE · IT · ES · FR · RU · ZH) on every plan';

export const CHECKOUT_NOTE = '🔒 Secure checkout by PayPro Global · VAT handled at checkout';

// ── FAQ ─────────────────────────────────────────────────────────────────────
// The two comparison answers link to pages that do not exist yet (phase 2:
// /coilmind/vs-siemens-copilot, /coilmind/vs-t-ia-connect). The questions ship
// now because people arrive already asking them; `href` gets filled in later.
export const FAQS = [
  { q: 'Which TIA versions and OS?', a: 'Windows only, because the Openness API is Windows-bound. TIA Portal V17 through V21, including the older versions that newer cloud assistants don’t support. I test day to day on V19–V21 and a few conveniences need V21, so on V17 and V18 expect the core to work and the edges to be thinner.\n\nOne thing to know before you download: Openness can only attach to a project that is already open in TIA Portal. Coilmind cannot open a project for you. You open it in TIA, then attach.' },
  { q: 'Can an LLM really write LAD, or is that just marketing?', a: 'LAD is graphical and a language model only emits text, so pasting pseudo-ladder and hoping was never going to work. Coilmind generates the logic, builds the block itself through the Openness API and gates every result on compilation. What lands in your project is valid LAD (or SCL), checked by the same compiler you’d use by hand. Reading and writing LAD reliably is where most of my development time went.' },
  {
    // Rewritten once Eigen's real terms were known. Naming what Eigen does
    // better is what makes the rest of the answer believable — the reader has
    // usually just come from the Siemens page.
    q: 'How is this different from the Siemens Eigen Engineering Agent?',
    a: 'Different products for different buyers. Eigen is £1,548.16 per seat per year, billed annually with no monthly option, and it needs TIA Portal V19 or newer plus an Industry Organization Account, which is a company-level thing. It does a lot Coilmind doesn’t: hardware, SINAMICS drives, PROFINET, creating WinCC Unified HMI devices.\n\nCoilmind is €29 to €149 a month, or yearly if you prefer, bought by one person with a card, and it works from V17. If you’re an organisation inside the Siemens ecosystem, theirs fits your procurement better. If you buy your own tools, that path is mostly closed to you.',
    more: 'Full comparison',
    href: '/coilmind/vs-siemens-copilot',
  },
  {
    q: 'Isn’t this the same as the TIA Portal MCP servers people are building?',
    a: 'The API underneath is similar. Those projects are middleware: you run a local server, drive it from a separate tool such as VS Code, Cursor or Claude Desktop, bring your own API key and pay the model provider yourself.\n\nCoilmind is one application with AI included: nothing else to install, no key to manage. The trade-off is that middleware gives you more raw API surface for building your own workflows. Coilmind is a finished tool.',
    more: 'Full comparison',
    href: null, // → /coilmind/vs-t-ia-connect when it exists
  },
  {
    q: 'Can two of us use one licence?',
    a: 'Yes. A licence covers several machines (two on Starter, four on Basic, ten on Premium) and you can work at the same time. The ceiling is requests per minute; there are no named seats. If you keep hitting the rate limit, that’s the point to move up a plan.',
  },
  { q: 'Can it work offline / air-gapped?', a: 'Yes, within a window. After activation a local model runs entirely on your machine and your code never leaves the PC. The licence has a 14-day offline grace period: work as long as you like with no connection, and within those 14 days reconnect briefly so it can refresh. That covers a commissioning trip or a stint on an isolated network. It is not permanent air-gapped operation, and no part of the product is: the licence check comes due either way.\n\nTwo more caveats. Local models are generally weaker than the cloud, and how capable they are depends on your GPU budget. The block templater and the generator need no model at all, online or offline.' },
  { q: 'Do I need my own API key?', a: 'No. AI is included. The provider keys live on our server, you pay one subscription, and there are no tokens to buy and nothing to configure on the model side.\n\nYour own key or a local model is optional, on every plan.' },
  { q: 'Can I use it without AI at all?', a: 'Yes. The Templater is fully local and deterministic, with no model calls, which helps where the security policy forbids AI.' },
  // Naming the subprocessors is the point of this answer: a German integrator
  // has to tell their client whose servers the code touched, and "a provider"
  // is not an answer they can pass on. The bare "GDPR-compliant" that used to
  // sit here was removed — to that reader it reads as filler, and stating the
  // mechanism (US transfer, named processors) is what actually reassures.
  //
  // Checked against both providers' published policies on 2026-08-14. Re-check
  // when either changes: Fireworks scopes its no-logging commitment to open
  // models, and DeepInfra reserves brief debugging retention — the two caveats
  // that make this answer accurate rather than merely reassuring.
  { q: 'Does my project content get sent to a model?', a: 'With cloud AI, your prompt and the project context it needs go to our inference providers, DeepInfra and Fireworks, and the answer comes straight back. Both are in the United States, so for an EU customer this is a transfer to the US; our privacy policy names them and our DPA sets out the basis. We keep no copy of your prompts or your project on our side.\n\nNeither provider uses the traffic to train models without an explicit opt-in, and we have not given one. The caveat to know before your client’s IT asks: DeepInfra states it may hold API inputs and outputs briefly for debugging. Nobody keeps them long-term.\n\nIf you don’t want any of that, use offline mode. With a local model nothing leaves the PC at all. The templater and the generator involve no model in the first place.' },
  { q: 'Will AI break my blocks?', a: 'Four things stand between the model and your project. Generated logic can be validated without being written at all: topology, schema and semantics are checked as a dry run. A local snapshot is taken before every block and HMI screen change, and restore is one click. A compile gate runs on every block import: if it doesn’t build, the change isn’t applied. And everything the assistant did is listed in a changelog inside the project.\n\nOne gap: snapshots cover blocks and screens, not tag, tag-table or UDT operations. Those you undo in TIA the usual way.' },
  { q: 'Why is this not just another AI wrapper?', a: 'It talks to TIA through the Openness API instead of working on uploaded files or pasted code, so it sees the project’s actual cross-references, UDTs and tag tables. If that difference doesn’t matter for your work, Coilmind isn’t for you.' },
  { q: 'What languages?', a: 'The UI ships in 7 languages (EN/DE/IT/ES/FR/RU/ZH). The AI answers in the language you ask in, and translation keeps the PLC context, so no machine pidgin.' },
  { q: 'How does pricing and the trial work?', a: `Three plans (Starter, Basic, Premium), paid monthly or yearly by card. No annual lock-in, no org account, AI included. ${TRIAL.faq} Checkout is handled by PayPro Global (VAT included), and you can upgrade, downgrade or cancel yourself from your account page.` },
];

// Deep links out of the landing. The homepage argues from benefit; the
// exhaustive list, the comparison and the honest limits live on their own
// pages. They used to be one-line links under the FAQ that nobody scrolled to,
// so they now get a block of their own right before it.
export const DECIDE = {
  eyebrow: 'Before you decide',
  h2: 'Check the details yourself',
  cards: [
    {
      href: '/coilmind/capabilities',
      title: 'Full capability list',
      body: 'Every feature by discipline, with its limits next to it. If it’s on the list, it works today.',
      more: 'See all capabilities',
    },
    {
      href: '/coilmind/vs-siemens-copilot',
      title: 'Coilmind vs Siemens Eigen',
      body: 'Price, TIA versions, offline work and what each one does better, side by side. Siemens wins three rows.',
      more: 'Read the comparison',
    },
    {
      href: '/coilmind/when-not-for-you',
      title: 'When Coilmind is not for you',
      body: 'The setups where Coilmind is the wrong tool. Worth reading before you start a trial.',
      more: 'Read the limits',
    },
  ],
};

export const CAPABILITIES_LINK = { label: 'Looking for something specific?', more: 'Full capability list', href: '/coilmind/capabilities' };

export const FOOTER = {
  // A closed contact, and someone on the other end of the email.
  tagline: 'Contact made.',
  by: 'Built and supported by one Siemens PLC engineer: Cirill at svghmi.pro · support@svghmi.pro',
  specs: 'Windows · TIA Portal V17–V21 · AI included, no tokens to buy.',
  disclaimer: 'AI-generated code must be reviewed by a qualified engineer · not for safety-critical applications.',
};

// ── Layout labels ───────────────────────────────────────────────────────────
// Every word Landing.jsx prints that is not part of a section's copy above:
// headings, rung captions, pricing-card units. The German bundle (content.de.js
// under /de/coilmind) exports the same object, so the layout never holds text.
export const UI = {
  tourCta: 'Watch the 70-second tour',
  rungNetwork: 'Network 1: Start',
  rungMonitoring: 'PLCSIM · monitoring',
  rungTitle: 'Click the contact to toggle the rung',
  rungOn: '%Q0.0 energized · block compiles ✓',
  rungOffBefore: 'click the contact',
  rungOffAfter: 'to energize the rung',
  rungCaption: 'LAD generated, imported and compiled in TIA through Openness.',
  rungCaptionTail: 'Not pasted pseudo-code.',
  painsEyebrow: 'Typical jobs',
  painsH2: 'Jobs that eat the week',
  featuresEyebrow: 'Features',
  featuresH2: 'What Coilmind does',
  diffEyebrow: 'Setup and pricing',
  diffH2: 'Where it differs from the cloud assistants',
  stepsEyebrow: 'How it works',
  stepsH2: 'Three steps to get started',
  safetyEyebrow: 'Undo and checks',
  safetyH2: 'If it gets a block or screen wrong, one click puts it back.',
  whoEyebrow: 'Who it’s for',
  whoH2: 'For freelancers and small integrators',
  solo: 'Solo',
  team: 'Small team',
  pricingEyebrow: 'Pricing',
  pricingH2: 'Three plans, priced per licence',
  pricingLead: 'Every plan is the full product, with AI included and no tokens to buy. The plans differ in volume. Upgrade, downgrade or cancel any time from your account page.',
  monthly: 'Monthly',
  yearly: 'Yearly',
  save: (pct) => `save ${pct}%`,
  twoMonthsFree: '2 months free',
  recommended: 'Recommended',
  perYear: ' / year',
  perMonth: (period) => ` / ${period}`,
  licenceLine: 'one licence · AI included',
  licenceLineYear: (saving) => `one licence · AI included · saves ${saving}`,
  tasksLine: (n) => `~${n} tasks / month`,
  devicesLine: (n) => `${n} devices`,
  reqLine: (n) => `${n} req / min`,
  bestFor: 'Best for ',
  // planBestFor: { starter, basic, premium } — overrides plan.js's English text
  planTrial: 'Start free trial',
  faqEyebrow: 'FAQ',
  faqH2: 'Common questions',
  numberLocale: 'en-US',
  money: (currency, amount) => `${currency}${amount}`,
  media: undefined, // the players' own English labels (media.jsx)
};
